import { useState } from "react";
import { toast } from "react-toastify";
import userServiceApi from "../api/userServiceApi";
import { getRequest, patchRequest } from "../api/http";
import { putRequest } from "../api/http";
import { TAB_KEYS } from "../config";
import { setMicroSiteActions } from "../store/actions";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useStore } from "../store";
import { setMicroSites } from "../store/actions";
import { isMicrositeApproved } from "../components/microsite-accordian/MicrositeHeader";
import {
  armRosieForNewSite,
  hasRosieWebsiteLink,
  isRosiePendingForSite,
  isSingleMicrositeOrg,
  markForceOpenRosieForSite,
  skipRosieIfNoWebsite,
} from "../utils/rosieNewSite";

export function useMicrosite() {
  const { micrositeId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { dispatch } = useStore();

  const [microSiteLoading, setLoading] = useState(false);
  const [microSitePublishLoading, setPublishLoading] = useState(false);
  const [microSiteUpdateLoading, setUpdateLoading] = useState(false);

  const headers = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  };

  const getMicroSites = async () => {
    setLoading(true);
    try {
      const response = await getRequest(userServiceApi.getAllMicrosites(), {
        headers,
      });
      if (response && response.data) {
        const data = response.data;
        if (data.length > 0) {
          const selectedSite = data.find(
            (site) => String(site.id) === String(micrositeId)
          );
          const selectedStillExists = Boolean(selectedSite);
          const selectedIsApproved =
            selectedStillExists &&
            isMicrositeApproved(selectedSite.approvalStatus);
          const firstApproved = data.find((site) =>
            isMicrositeApproved(site.approvalStatus)
          );

          dispatch(setMicroSites(data));

          const pendingId = localStorage.getItem("newlyCreatedMicroSiteId");
          const pendingSite = pendingId
            ? data.find((site) => String(site.id) === String(pendingId))
            : null;
          // No website → do not open Site Features + Rosie for this (or any other) site.
          skipRosieIfNoWebsite(pendingSite);

          // First site after org-details save: arm Rosie for later first open.
          if (sessionStorage.getItem("armRosieForFirstMicrosite") === "true") {
            sessionStorage.removeItem("armRosieForFirstMicrosite");
            if (hasRosieWebsiteLink(data[0])) {
              armRosieForNewSite(data[0]?.id);
            }
          } else if (
            pendingSite &&
            !isMicrositeApproved(pendingSite.approvalStatus) &&
            localStorage.getItem("isNewlyCreatedMicroSite") === "true" &&
            hasRosieWebsiteLink(pendingSite)
          ) {
            // Survive refreshes while Waiting for Approval: keep pending arm.
            armRosieForNewSite(pendingSite.id);
          }

          // Only navigate when URL has no valid *approved* microsite selected.
          // Waiting / Rejected sites must stay collapsed (no auto-open).
          const skipAutoOpen =
            sessionStorage.getItem("skipAutoOpenMicrosite") === "true";
          if (skipAutoOpen) {
            sessionStorage.removeItem("skipAutoOpenMicrosite");
          }

          // Accordion auto-open on refresh stays as-is.
          // Rosie on auto-open ONLY for a brand-new org with a single site + website.
          // Multi-site: Rosie still requires a manual accordion click.
          const allowRosieOnAutoOpen = (site) => {
            if (!site || !isSingleMicrositeOrg(data)) return;
            if (!hasRosieWebsiteLink(site)) {
              skipRosieIfNoWebsite(site);
              return;
            }
            if (isRosiePendingForSite(site.id)) {
              markForceOpenRosieForSite(site.id);
            }
          };

          if (selectedIsApproved) {
            localStorage.setItem("micrositeId", micrositeId);
            allowRosieOnAutoOpen(selectedSite);
          } else if (selectedStillExists && !selectedIsApproved) {
            // Stale / deep link to a locked site — collapse to home.
            localStorage.setItem("micrositeId", selectedSite.id);
            navigate(`/`, {
              replace: true,
              state: state ? state : TAB_KEYS.aboutUs,
            });
          } else if (!skipAutoOpen && firstApproved) {
            localStorage.setItem("micrositeId", firstApproved.id);
            allowRosieOnAutoOpen(firstApproved);
            navigate(`/${firstApproved.id}`, {
              state: state ? state : TAB_KEYS.aboutUs,
            });
          } else if (!selectedStillExists && data[0]?.id) {
            // Keep a default id in storage but do not open non-approved sites.
            localStorage.setItem("micrositeId", data[0].id);
          }
          setLoading(false);
        } else {
          localStorage.removeItem("micrositeId");
          navigate('/edit-organization', { state: { tab: "organizationDetailsTab" } })
        }
      }
    } catch (error) {
      console.log('error', error)
    }
    finally {
      setLoading(false);
    }
  };

  const updateMicroSite = async (
    micrositeId,
    data,
    reloadFlagFunction,
    page
  ) => {
    try {
      setUpdateLoading(true);
      const response = await putRequest(
        userServiceApi.publishMicrosite(micrositeId),
        data,
        { headers: { "Content-Type": "multipart/form-data", ...headers } }
      );
      if (response) {
        setUpdateLoading(false);
        if (page === "home") {
          getMicroSites();
        }
        if (page === "manageMicrosite") {
          reloadFlagFunction();
        }
        toast.success("Microsite updated successfully");
        dispatch(
          setMicroSiteActions({
            activeKey: micrositeId,
            menuKey: "edit",
            change: false,
          })
        );
      } else {
        setUpdateLoading(false);
      }
    } catch (error) {
      setUpdateLoading(false);

      toast.error(error);
    }
  };

  const publishMicroSite = async (micrositeId, status) => {
    try {
      setPublishLoading(true);
      const response = await patchRequest(
        userServiceApi.publishMicrosite(micrositeId),
        {
          headers,
        }
      );
      if (response) {
        setPublishLoading(false);
        toast.success(`Site ${status} successfully`);
        localStorage.setItem("switchSuccess", true);
        getMicroSites();
        return false;
      }
    } catch (error) {
      setPublishLoading(false);
      localStorage.setItem("switchSuccess", false);
      toast.error(error.error);
      return false;
    }
  };

  return {
    getMicroSites,
    microSiteLoading,
    publishMicroSite,
    microSitePublishLoading,
    updateMicroSite,
    microSiteUpdateLoading,
  };
}
